import { useEffect, useMemo, useRef, useState } from 'react';
import {
  onAuthStateChanged, signInWithEmailAndPassword, signOut,
} from 'firebase/auth';
import {
  doc, setDoc, onSnapshot, collection, query, orderBy,
  deleteDoc, getDocs, serverTimestamp,
} from 'firebase/firestore';
import { auth, db } from '../firebase';
import { TOOLS } from './Home';

// Password-protected admin console (route /admin, unlinked from the site).
//
// Sign in with the admin account you create in the Firebase console
// (Authentication → Users, Email/Password provider). Firestore rules only grant
// write access to config/announcement, and read access to contactMessages /
// canvass listing, to that specific account (see firestore.rules → isAdmin()).
// toolUsage's aggregate counts are publicly readable (see firestore.rules)
// since Home.js also reads them client-side to rank tool cards.
//
// From here you can, WITHOUT redeploying:
//   • see per-tool visits vs. actual uses (file uploaded / analysis run / etc.)
//   • toggle & edit the site-wide announcement banner
//   • pick & reorder the dashboard's "Featured Tools" section
//   • read (and delete) contact-form submissions
//   • list every Video Canvass with its point count, and delete stale ones
//   • list every Area Search with its participant/pin counts, and delete stale ones

// ── shared styles ──
const page = { minHeight: '100vh', background: '#f8fafc', color: '#0f172a', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" };
const card = { background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' };
const label = { fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '.05em', display: 'block', marginBottom: 6 };
const input = { width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14, fontFamily: 'inherit', outline: 'none' };
const btn = { padding: '10px 18px', borderRadius: 8, border: 'none', fontSize: 14, fontWeight: 700, color: '#fff', background: 'linear-gradient(135deg, #4f46e5, #7c3aed)', cursor: 'pointer' };
const btnGhost = { padding: '8px 14px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, fontWeight: 600, color: '#334155', background: '#fff', cursor: 'pointer' };
const sectionTitle = { fontSize: 12, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: '#64748b', margin: '0 0 14px' };

function fmtDate(ts) {
  const d = ts && typeof ts.toDate === 'function' ? ts.toDate() : (ts instanceof Date ? ts : null);
  return d ? d.toLocaleString() : '—';
}

// ── Tool usage ──
//
// Every tool page fires two separate counters (src/utils/metrics.js):
// "visits" (trackToolUse — the page was opened) and "uses" (trackToolAction
// — the tool was actually run: a file was uploaded, an analysis started, a
// document generated, etc., called explicitly by each tool page at the
// moment it does real work). This panel is what makes that distinction
// visible — a tool with lots of visits but few uses is being looked at,
// not used.
//
// Day-by-day history also exists (toolUsage/{tool}/daily/{date}) but its
// Firestore rule is locked to console-only reads (see firestore.rules) —
// only the aggregate totals here are exposed to the client.
function ToolUsagePanel() {
  const [rows, setRows] = useState(null);
  const [sortKey, setSortKey] = useState('runs'); // 'runs' | 'count'
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'toolUsage'),
      snap => setRows(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      () => setRows([]),
    );
    return unsub;
  }, []);

  const sorted = useMemo(() => {
    if (!rows) return [];
    return [...rows].sort((a, b) => (b[sortKey] || 0) - (a[sortKey] || 0) || (b.count || 0) - (a.count || 0));
  }, [rows, sortKey]);

  const unusedCount = useMemo(() => sorted.filter(t => !t.runs).length, [sorted]);
  const visibleRows = showAll ? sorted : sorted.filter(t => t.runs);

  const thSort = (key, label) => (
    <th onClick={() => setSortKey(key)} style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, fontSize: 11, color: sortKey === key ? '#0f172a' : '#64748b', textTransform: 'uppercase', letterSpacing: '.05em', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
      {label}{sortKey === key ? ' ▼' : ''}
    </th>
  );

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ ...sectionTitle, margin: 0 }}>Tool usage {rows ? `(${rows.length})` : ''}</h2>
        {unusedCount > 0 && (
          <button style={btnGhost} onClick={() => setShowAll(v => !v)}>
            {showAll ? 'Hide unused tools' : `Show all (${unusedCount} with 0 uses hidden)`}
          </button>
        )}
      </div>
      <p style={{ fontSize: 12.5, color: '#64748b', margin: '10px 0 14px', lineHeight: 1.5 }}>
        Visits = the page was opened. Uses = the tool actually did something — a file was uploaded, an
        analysis was run, a document was generated. A tool with many visits but few uses is being looked
        at, not used.
      </p>
      {rows === null && <p style={{ fontSize: 13, color: '#64748b' }}>Loading…</p>}
      {rows && rows.length === 0 && <p style={{ fontSize: 13, color: '#64748b' }}>No usage recorded yet.</p>}
      {rows && rows.length > 0 && visibleRows.length === 0 && (
        <p style={{ fontSize: 13, color: '#64748b' }}>Every tracked tool has 0 uses so far. Click "Show all" above to see them.</p>
      )}
      {visibleRows.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.05em' }}>Tool</th>
                {thSort('count', 'Visits')}
                {thSort('runs', 'Uses')}
                <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.05em' }}>Use Rate</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.05em', whiteSpace: 'nowrap' }}>Last Used</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map(t => {
                const visits = t.count || 0, uses = t.runs || 0;
                const rate = visits > 0 ? Math.round((uses / visits) * 100) : null;
                const rateColor = rate == null ? '#cbd5e1' : rate >= 50 ? '#059669' : rate >= 20 ? '#b45309' : '#dc2626';
                return (
                  <tr key={t.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '8px 10px', fontWeight: 600 }}>{t.tool || t.id}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', color: '#475569' }}>{visits.toLocaleString()}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, color: '#0f172a' }}>{uses.toLocaleString()}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', color: rateColor, fontWeight: 600 }}>{rate == null ? '—' : `${rate}%`}</td>
                    <td style={{ padding: '8px 10px', fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>{t.runs ? fmtDate(t.lastRunAt) : 'never used'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Login ──
function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async e => {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
    } catch {
      setErr('Sign-in failed. Check the email and password.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ ...page, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <form onSubmit={submit} style={{ ...card, width: '100%', maxWidth: 380 }}>
        <h1 style={{ fontSize: 20, fontWeight: 800, margin: '0 0 4px' }}>Admin sign-in</h1>
        <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 20px' }}>LE Tech Tools console</p>
        <div style={{ marginBottom: 14 }}>
          <label style={label}>Email</label>
          <input style={input} type="email" value={email} onChange={e => setEmail(e.target.value)} autoFocus required />
        </div>
        <div style={{ marginBottom: 18 }}>
          <label style={label}>Password</label>
          <input style={input} type="password" value={password} onChange={e => setPassword(e.target.value)} required />
        </div>
        {err && <div style={{ fontSize: 13, color: '#dc2626', marginBottom: 14 }}>{err}</div>}
        <button type="submit" style={{ ...btn, width: '100%', opacity: busy ? 0.6 : 1 }} disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  );
}

// ── Announcement editor ──
function AnnouncementEditor() {
  const [form, setForm] = useState({ enabled: false, message: '', level: 'info', linkText: '', linkUrl: '' });
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const seeded = useRef(false);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'config', 'announcement'), snap => {
      if (!seeded.current) {
        seeded.current = true;
        const d = snap.exists() ? snap.data() : {};
        setForm({
          enabled: !!d.enabled, message: d.message || '', level: d.level || 'info',
          linkText: d.linkText || '', linkUrl: d.linkUrl || '',
        });
      }
    });
    return unsub;
  }, []);

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setSaved(false); };

  const save = async () => {
    setBusy(true);
    try {
      await setDoc(doc(db, 'config', 'announcement'), {
        enabled: !!form.enabled,
        message: form.message.trim(),
        level: form.level,
        linkText: form.linkText.trim(),
        linkUrl: form.linkUrl.trim(),
        updatedAt: serverTimestamp(),
      });
      setSaved(true);
    } catch {
      alert('Could not save. Make sure you are signed in as the admin account.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={card}>
      <h2 style={sectionTitle}>Announcement banner</h2>
      <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
        <input type="checkbox" checked={form.enabled} onChange={e => set('enabled', e.target.checked)} style={{ width: 18, height: 18 }} />
        Show the banner on the site {form.enabled ? '(live)' : '(hidden)'}
      </label>
      <div style={{ marginBottom: 14 }}>
        <label style={label}>Message</label>
        <textarea style={{ ...input, minHeight: 70, resize: 'vertical' }} value={form.message}
          maxLength={500} onChange={e => set('message', e.target.value)} placeholder="e.g. The LE Tech Tools iOS app is now available!" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 14, marginBottom: 14 }}>
        <div>
          <label style={label}>Style</label>
          <select style={input} value={form.level} onChange={e => set('level', e.target.value)}>
            <option value="info">Info (indigo)</option>
            <option value="success">Success (green)</option>
            <option value="warn">Warning (amber)</option>
          </select>
        </div>
        <div>
          <label style={label}>Link text (optional)</label>
          <input style={input} value={form.linkText} maxLength={80} onChange={e => set('linkText', e.target.value)} placeholder="Download now" />
        </div>
        <div>
          <label style={label}>Link URL (optional)</label>
          <input style={input} value={form.linkUrl} maxLength={500} onChange={e => set('linkUrl', e.target.value)} placeholder="https://apps.apple.com/…" />
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <button style={{ ...btn, opacity: busy ? 0.6 : 1 }} onClick={save} disabled={busy}>
          {busy ? 'Saving…' : 'Save banner'}
        </button>
        {saved && <span style={{ fontSize: 13, color: '#059669', fontWeight: 600 }}>Saved — live on the site.</span>}
      </div>
    </div>
  );
}

// ── Featured tools ──
//
// Drives the "Featured Tools" section on the dashboard (Home.js), stored as
// `config/featuredTools: { toolPaths: [...] }` — an ordered list of tool
// `to` paths. Same live-edit-without-redeploy pattern as the announcement
// banner above; Home.js hides the section entirely when the list is empty.
function FeaturedToolsEditor() {
  const [selected, setSelected] = useState([]);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const seeded = useRef(false);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'config', 'featuredTools'), snap => {
      if (!seeded.current) {
        seeded.current = true;
        const d = snap.exists() ? snap.data() : {};
        const paths = Array.isArray(d.toolPaths) ? d.toolPaths : [];
        setSelected(paths.filter(p => TOOLS.some(t => t.to === p)));
      }
    });
    return unsub;
  }, []);

  const add = (to) => { if (to && !selected.includes(to)) { setSelected(s => [...s, to]); setSaved(false); } };
  const remove = (to) => { setSelected(s => s.filter(p => p !== to)); setSaved(false); };
  const move = (to, dir) => {
    setSelected(prev => {
      const i = prev.indexOf(to);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
    setSaved(false);
  };

  const save = async () => {
    setBusy(true);
    try {
      await setDoc(doc(db, 'config', 'featuredTools'), { toolPaths: selected, updatedAt: serverTimestamp() });
      setSaved(true);
    } catch {
      alert('Could not save. Make sure you are signed in as the admin account.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={card}>
      <h2 style={sectionTitle}>Featured tools</h2>
      <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 14px', lineHeight: 1.6 }}>
        Shown as a highlighted section on the dashboard, just under the "Top tools" row. Empty = section hidden.
      </p>

      {selected.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
          {selected.map((to, i) => {
            const tool = TOOLS.find(t => t.to === to);
            if (!tool) return null;
            return (
              <div key={to} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
                border: '1px solid #e2e8f0', borderRadius: 8, background: '#f8fafc',
              }}>
                <span style={{ fontSize: 13.5, fontWeight: 600, flex: 1 }}>{tool.title}</span>
                <button style={{ ...btnGhost, padding: '4px 9px' }} onClick={() => move(to, -1)} disabled={i === 0} title="Move up">↑</button>
                <button style={{ ...btnGhost, padding: '4px 9px' }} onClick={() => move(to, 1)} disabled={i === selected.length - 1} title="Move down">↓</button>
                <button style={{ ...btnGhost, padding: '4px 9px', color: '#b91c1c' }} onClick={() => remove(to)}>Remove</button>
              </div>
            );
          })}
        </div>
      )}

      <label style={label}>Add a tool</label>
      <select style={input} value="" onChange={e => add(e.target.value)}>
        <option value="">Select a tool to add…</option>
        {TOOLS.filter(t => !selected.includes(t.to)).map(t => (
          <option key={t.to} value={t.to}>{t.title}</option>
        ))}
      </select>

      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 16 }}>
        <button style={{ ...btn, opacity: busy ? 0.6 : 1 }} onClick={save} disabled={busy}>
          {busy ? 'Saving…' : 'Save featured tools'}
        </button>
        {saved && <span style={{ fontSize: 13, color: '#059669', fontWeight: 600 }}>Saved — live on the site.</span>}
      </div>
    </div>
  );
}

// ── Contact inbox ──
function ContactInbox() {
  const [msgs, setMsgs] = useState(null);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'contactMessages'), orderBy('createdAt', 'desc')),
      snap => setMsgs(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      () => setMsgs([]),
    );
    return unsub;
  }, []);

  const remove = async id => {
    if (!window.confirm('Delete this message?')) return;
    try { await deleteDoc(doc(db, 'contactMessages', id)); } catch { alert('Delete failed.'); }
  };

  return (
    <div style={card}>
      <h2 style={sectionTitle}>Contact inbox {msgs ? `(${msgs.length})` : ''}</h2>
      {msgs === null && <p style={{ fontSize: 13, color: '#64748b' }}>Loading…</p>}
      {msgs && msgs.length === 0 && <p style={{ fontSize: 13, color: '#64748b' }}>No messages yet.</p>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {msgs && msgs.map(m => (
          <div key={m.id} style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 14, fontWeight: 700 }}>
                {m.name || 'Anonymous'}
                {m.email && (
                  <a href={`mailto:${m.email}`} style={{ fontWeight: 500, color: '#4f46e5', marginLeft: 8, fontSize: 13, textDecoration: 'none' }}>
                    {m.email}
                  </a>
                )}
              </div>
              <span style={{ fontSize: 12, color: '#94a3b8' }}>{fmtDate(m.createdAt)}</span>
            </div>
            <p style={{ fontSize: 14, color: '#334155', lineHeight: 1.6, margin: '8px 0 10px', whiteSpace: 'pre-wrap' }}>{m.message}</p>
            <button style={btnGhost} onClick={() => remove(m.id)}>Delete</button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Video canvasses ──
function CanvassManager() {
  const [rows, setRows] = useState(null);
  const [counts, setCounts] = useState({});

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'canvasses'), orderBy('createdAt', 'desc')),
      snap => {
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setRows(list);
        // Fetch point counts (one lightweight read per canvass).
        list.forEach(async c => {
          try {
            const ps = await getDocs(collection(db, 'canvasses', c.id, 'points'));
            setCounts(prev => ({ ...prev, [c.id]: ps.size }));
          } catch { /* leave count unknown */ }
        });
      },
      () => setRows([]),
    );
    return unsub;
  }, []);

  const removeCanvass = async c => {
    if (!window.confirm(`Delete "${c.title || 'Untitled'}" and all of its locations? This cannot be undone.`)) return;
    try {
      const ps = await getDocs(collection(db, 'canvasses', c.id, 'points'));
      await Promise.all(ps.docs.map(p => deleteDoc(doc(db, 'canvasses', c.id, 'points', p.id))));
      await deleteDoc(doc(db, 'canvasses', c.id));
    } catch {
      alert('Delete failed.');
    }
  };

  return (
    <div style={card}>
      <h2 style={sectionTitle}>Video canvasses {rows ? `(${rows.length})` : ''}</h2>
      {rows === null && <p style={{ fontSize: 13, color: '#64748b' }}>Loading…</p>}
      {rows && rows.length === 0 && <p style={{ fontSize: 13, color: '#64748b' }}>No canvasses created yet.</p>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {rows && rows.map(c => (
          <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 14px' }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700 }}>{c.title || 'Untitled canvass'}</div>
              {c.subjectDesc && <div style={{ fontSize: 12.5, color: '#64748b', marginTop: 2 }}>Subject: {c.subjectDesc}</div>}
              <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
                Created {fmtDate(c.createdAt)} · Last activity {fmtDate(c.lastActivityAt)} ·{' '}
                {counts[c.id] == null ? '…' : `${counts[c.id]} location${counts[c.id] === 1 ? '' : 's'}`}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
              <a href={`/canvass/${c.id}`} target="_blank" rel="noopener noreferrer" style={{ ...btnGhost, textDecoration: 'none', display: 'inline-block' }}>Open</a>
              <button style={{ ...btnGhost, color: '#dc2626', borderColor: '#fecaca' }} onClick={() => removeCanvass(c)}>Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Area searches ──
function AreaSearchManager() {
  const [rows, setRows] = useState(null);
  const [counts, setCounts] = useState({});

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'areaSearches'), orderBy('createdAt', 'desc')),
      snap => {
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setRows(list);
        // Fetch participant/pin counts (two lightweight reads per search).
        list.forEach(async s => {
          try {
            const [participants, pins] = await Promise.all([
              getDocs(collection(db, 'areaSearches', s.id, 'participants')),
              getDocs(collection(db, 'areaSearches', s.id, 'pins')),
            ]);
            setCounts(prev => ({ ...prev, [s.id]: { participants: participants.size, pins: pins.size } }));
          } catch { /* leave counts unknown */ }
        });
      },
      () => setRows([]),
    );
    return unsub;
  }, []);

  const removeSearch = async s => {
    if (!window.confirm(`Delete "${s.title || 'Untitled'}" and all of its participants, trail points, and pins? This cannot be undone.`)) return;
    try {
      const [participants, trailPoints, pins] = await Promise.all([
        getDocs(collection(db, 'areaSearches', s.id, 'participants')),
        getDocs(collection(db, 'areaSearches', s.id, 'trailPoints')),
        getDocs(collection(db, 'areaSearches', s.id, 'pins')),
      ]);
      await Promise.all([
        ...participants.docs.map(p => deleteDoc(doc(db, 'areaSearches', s.id, 'participants', p.id))),
        ...trailPoints.docs.map(t => deleteDoc(doc(db, 'areaSearches', s.id, 'trailPoints', t.id))),
        ...pins.docs.map(p => deleteDoc(doc(db, 'areaSearches', s.id, 'pins', p.id))),
      ]);
      await deleteDoc(doc(db, 'areaSearches', s.id));
    } catch {
      alert('Delete failed.');
    }
  };

  const boundaryLabel = b => {
    if (!b) return '';
    if (b.type === 'circle') return `circle, ${Math.round(b.radiusMeters || 0)}m radius`;
    if (b.type === 'polygon') return `polygon, ${(b.points || []).length} points`;
    return '';
  };

  return (
    <div style={card}>
      <h2 style={sectionTitle}>Area searches {rows ? `(${rows.length})` : ''}</h2>
      {rows === null && <p style={{ fontSize: 13, color: '#64748b' }}>Loading…</p>}
      {rows && rows.length === 0 && <p style={{ fontSize: 13, color: '#64748b' }}>No area searches created yet.</p>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {rows && rows.map(s => {
          const c = counts[s.id];
          return (
            <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 14px' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700 }}>{s.title || 'Untitled search'}</div>
                {s.vehicleDesc && <div style={{ fontSize: 12.5, color: '#64748b', marginTop: 2 }}>{s.vehicleDesc}</div>}
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
                  Created {fmtDate(s.createdAt)} · Last activity {fmtDate(s.lastActivityAt)} ·{' '}
                  {c == null ? '…' : `${c.participants} participant${c.participants === 1 ? '' : 's'} · ${c.pins} pin${c.pins === 1 ? '' : 's'}`}
                  {s.boundary && ` · ${boundaryLabel(s.boundary)}`}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                <a href={`/areasearch/${s.id}`} target="_blank" rel="noopener noreferrer" style={{ ...btnGhost, textDecoration: 'none', display: 'inline-block' }}>Open</a>
                <button style={{ ...btnGhost, color: '#dc2626', borderColor: '#fecaca' }} onClick={() => removeSearch(s)}>Delete</button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Admin() {
  const [user, setUser] = useState(undefined); // undefined = still checking

  useEffect(() => onAuthStateChanged(auth, u => setUser(u)), []);

  if (user === undefined) {
    return <div style={{ ...page, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <span style={{ color: '#64748b', fontSize: 14 }}>Loading…</span>
    </div>;
  }
  if (!user) return <Login />;

  return (
    <div style={page}>
      <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0' }}>
        <div style={{ maxWidth: 900, margin: '0 auto', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 16, fontWeight: 800 }}>LE Tech Tools · Admin</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 13, color: '#64748b' }}>{user.email}</span>
            <button style={btnGhost} onClick={() => signOut(auth)}>Sign out</button>
          </div>
        </div>
      </div>
      <main style={{ maxWidth: 900, margin: '0 auto', padding: '24px 24px 72px', display: 'flex', flexDirection: 'column', gap: 20 }}>
        <ToolUsagePanel />
        <AnnouncementEditor />
        <FeaturedToolsEditor />
        <ContactInbox />
        <CanvassManager />
        <AreaSearchManager />
      </main>
    </div>
  );
}

export default Admin;
